
import Image from "./Image";
import { useCallback, useEffect, useRef, useState } from "react";
/* The combined "Rental Back Office" + "Direct Rental" network.

   Left to right: the six Rentid product marks each send a color-matched beam into the
   central "Rental Brand" hub. From the hub the flow passes through a compact channel
   column (Online / In person) and fans out to a cluster of renters — a
   brand-green beam flows outward (hub -> devices -> renters) and a Turo-purple beam flows
   back inward (renters -> devices -> hub), showing the two-way direct-rental relationship.

   The card animates once, armed by the `.seq-on` class its wrapper gains when the deck
   scrolls into view (see globals.css). The sequence plays a single time and never
   restarts:
     1) the rentID card wordmark sheen sweeps once,
     2) these six product logos light up to full and stay at 100%,
     3) the beams begin flowing and loop continuously.

   Hovering any node (renter, device, or the hub) reveals a small label. There is no
   full-card hover wash — the diagram stays live.

   Layout runs in a 620x540 coordinate space; nodes are positioned as percentages of that
   space so the HTML overlay stays aligned with the SVG beams regardless of the card's
   actual aspect ratio (beams use preserveAspectRatio="none"). */
const VB_W = 620;
const VB_H = 540;
/* Product beams converge here, at the "Rental Brand" hub. */
const HUB = { x: 250, y: 270 };
/* Channel column — two nodes: Online (rental website on a tablet) and In person (handshake
   key-handover). Each node's y is centered on the three renters that use that channel. */
const DEVICE_X = 352;
const DEVICE_Y = [138, 402];
/* Renter cluster + vehicle column — pulled a little closer to the channel nodes so the
   people/vehicles read as directly connected to Online / In person. */
const RENTER_X = 432;
/* Vehicle column on the far right: each renter shown with the vehicle they rent. */
const VEHICLE_X = 554;
/* Left edge where the horizontal product logos are anchored. */
const LOGO_X = 24;
/* Product logo rows — evenly spaced (88 units apart) and centered vertically on the hub so
   the six marks are balanced top-to-bottom and their beams fan symmetrically into the hub. */
const ROW_Y = [50, 138, 226, 314, 402, 490];
/* Brand green (matches rentdirect) and Turo purple, reused for the network beams. */
const GREEN = "#14E0A5";
const TURO_PURPLE = "#593CFB";
/* Order top-to-bottom: direct, id, shield, ai, success, academy — each with the brand's
   main color (used for the row's beam). Full horizontal logo SVGs (icon + white
   wordmark), rendered bare.

   `beamStart` is a viewBox-x FALLBACK for first paint / SSR; at runtime the true start is
   measured from the rendered logo so beams touch the wordmark at any width (see `measure`).

   `contentFrac` is the fraction of each SVG's box width at which its artwork visibly ends
   (from getBBox on the source file). The SVGs carry trailing transparent padding — most
   notably rentAI at ~0.66 — so we multiply the rendered logo width by this to find where
   the last letter actually is.

   `scale` normalizes the visible lockup size. Every logo's wordmark is drawn at ~the same
   cap height in its own units, but the SVGs have different viewBox heights (88-107), so a
   single fixed row height renders the taller-viewBox marks (rentID, rentshield) too small.
   These per-logo multipliers equalize the wordmark cap height to rentacademy (the
   reference), measured from each SVG's actual wordmark bounding box. */
const PRODUCTS = [
    { name: "rentdirect", src: "/products/rentdirect.svg", color: "#14E0A5", beamStart: 112, contentFrac: 0.864, scale: 0.99, tip: "Booking engine" },
    { name: "rentID", src: "/products/rentid.svg", color: "#29B6E8", beamStart: 94, contentFrac: 0.902, scale: 1.17, tip: "Driver verification tools" },
    { name: "rentshield", src: "/products/rentshield.svg", color: "#F5455F", beamStart: 113, contentFrac: 0.917, scale: 1.19, tip: "Insurance & protections" },
    { name: "rentai", src: "/products/rentai.svg", color: "#4597F8", beamStart: 91, contentFrac: 0.655, scale: 0.98, tip: "Automations & efficiencies" },
    { name: "rentsuccess", src: "/products/rentsuccess.svg", color: "#6A4BFF", beamStart: 131, contentFrac: 0.991, scale: 1.05, tip: "Resolutions & incidents" },
    { name: "rentacademy", src: "/products/rentacademy.svg", color: "#FF9F0A", beamStart: 131, contentFrac: 0.995, scale: 1.0, tip: "Support & training" },
];
/* Channel nodes: Online (the rental website shown on a tablet) and In person (a handshake
   key-handover). The photo fills the circle; hovering reveals the "Online" / "In person"
   badge. */
const DEVICES = [
    { key: "online", src: "/images/devices/online-tablet.webp", label: "Online" },
    { key: "in-person", src: "/images/devices/in-person.webp", label: "In person" },
];
/* Renters — real faces dressed for each renter type found in rent-to-anyone fleets. Each
   is tied to one of the three devices so its rails route through that device. Evenly spaced
   (matching the vehicle column) so the circular faces never overlap and each renter aligns
   horizontally with its vehicle. */
const RENTERS = [
    { y: 50, device: 0, src: "/images/guests/leisure.webp", label: "Leisure travelers", short: "Leisure" },
    { y: 138, device: 0, src: "/images/guests/rideshare.webp", label: "Rideshare drivers", short: "Rideshare" },
    { y: 226, device: 0, src: "/images/guests/delivery.webp", label: "Delivery drivers", short: "Delivery" },
    { y: 314, device: 1, src: "/images/guests/packages.webp", label: "Package couriers", short: "Courier" },
    { y: 402, device: 1, src: "/images/guests/corporate.webp", label: "Business travelers", short: "Business" },
    { y: 490, device: 1, src: "/images/guests/student.webp", label: "Speciality", short: "Speciality" },
];
/* Vehicle column — each renter shown with the vehicle they actually rent. Each vehicle sits
   at the SAME y as its renter (by index) so every person→vehicle rail is a perfectly
   horizontal, uncrooked line. */
const VEHICLES = [
    { y: 50, src: "/images/vehicles/leisure.webp", label: "Personal SUV" },
    { y: 138, src: "/images/vehicles/rideshare.webp", label: "Rideshare sedan" },
    { y: 226, src: "/images/vehicles/delivery.webp", label: "Delivery car" },
    { y: 314, src: "/images/vehicles/packages.webp", label: "Cargo van" },
    { y: 402, src: "/images/vehicles/corporate.webp", label: "Electric sedan" },
    { y: 490, src: "/images/vehicles/student.webp", label: "Campus hatchback" },
];
/* Product beams: a smooth symmetric S from the end of each logo's wordmark into the hub.
   `starts` are the per-logo x (viewBox units) where each wordmark visibly ends — measured
   at runtime so the beams always start right at the last letter for any card width. */
function makePaths(starts) {
    return ROW_Y.map((y, i) => {
        const start = starts[i];
        const cOffset = (HUB.x - start) * 0.5;
        return `M ${start} ${y} C ${start + cOffset} ${y} ${HUB.x - cOffset} ${HUB.y} ${HUB.x} ${HUB.y}`;
    });
}
/* Flowing rails for the network. The hub<->device and device<->person rails use the SAME
   smooth horizontal-tangent S-curve as the product beams on the left (see makePaths /
   sCurveTo), so both sides of the diagram share one organic style. The person<->vehicle rail
   deliberately stays a straight line. These faint rails are static structure; the animated
   beams travel the continuous per-person journeys defined in JOURNEYS below. */
function sCurveTo(x1, y1, x2, y2) {
    const c = (x2 - x1) * 0.5;
    return `C ${x1 + c} ${y1} ${x2 - c} ${y2} ${x2} ${y2}`;
}
const HUB_DEVICE = DEVICE_Y.map((y) => `M ${HUB.x} ${HUB.y} ${sCurveTo(HUB.x, HUB.y, DEVICE_X, y)}`);
/* Device -> person rail, recomputed at runtime so its right endpoint always lands exactly on
   the person's CURRENT x (see `deviceRenterRails`). During the entrance the people slide in
   from the far right, and the rail's end tracks them frame-by-frame, so a person is never
   shown detached from its Online / In person node. */
function makeDeviceRenter(personX) {
    return RENTERS.map((r) => `M ${DEVICE_X} ${DEVICE_Y[r.device]} ${sCurveTo(DEVICE_X, DEVICE_Y[r.device], personX, r.y)}`);
}
/* Rail from each renter (their row) out to their paired vehicle (its own row). */
const RENTER_VEHICLE = VEHICLES.map((v, i) => `M ${RENTER_X} ${RENTERS[i].y} L ${VEHICLE_X} ${v.y}`);
/* Horizontal midpoint of the person→vehicle gap — where each row's label badge sits. */
const MID_X = (RENTER_X + VEHICLE_X) / 2;
/* Each renter's relationship travels the network as a chain of SHORT, single-segment beams
   that hop node-to-node with a brief PAUSE at every stop — so a beam never streaks across the
   whole map, and every leg moves at the same calm speed as the product beams on the left.

   Per person, one full round trip is six segments fired in order:
     OUT  (product color): 1) hub -> device, 2) device -> person, 3) person -> vehicle,
     BACK (Turo purple):   4) vehicle -> person, 5) person -> device,
     BACK (brand green):   6) device -> hub  (the only beam that re-enters the hub).
   Colors keep their meaning: the whole product palette flows OUT of the hub, purple returns
   along the outer rails without touching the hub, and only green ever flows back INTO it. So
   the beams pause at the device, at the person and at the vehicle exactly as required, and
   nothing ever races straight from the hub to a person or vehicle.

   Timing: each segment's launch (`--d`, seconds) is the previous segment's travel time plus a
   PAUSE, which reads as the energy stopping AT the node before moving on. A new journey starts
   every JOURNEY_STRIDE; with the ~9.5s round trip that keeps ~3-4 journeys in flight across
   different regions. The shared 18s CSS period (= 6 × stride) keeps every segment's travel
   window — and therefore its speed — identical. */
/* Steady-state relay pace. Every timing below is scaled ~1.4x slower than the original tuning
   so that, once the energetic intro settles into the looping relay, the flow reads calmer still.
   The scale is uniform (period, per-segment travel windows AND launch delays all ×1.4) so the
   "constant speed with a pause at each node" relationship is preserved exactly, and the CSS loop
   period stays equal to 6 × JOURNEY_STRIDE (now 25.2s, matching the seq-seg-* animations). */
const RELAY_BASE = 3.08;
const JOURNEY_STRIDE = 4.2;
/* Per-segment travel times (seconds). hub<->device is the long leg (DUR_HD). device<->person
   is the med leg (DUR_DP). person<->vehicle (DUR_PV) deliberately runs SLOWER — it shares the
   long-leg travel window so the wide straight rail reads as calm rather than zippy, matching the
   unhurried pace of the rest of the brand's beams (see .seq-seg-long / .seq-seg-med). */
const DUR_HD = 2.1;
const DUR_DP = 1.54;
const DUR_PV = 2.14;
/* Pause held at each intermediate node, with a slightly longer turnaround at the vehicle. */
const PAUSE = 0.63;
const PAUSE_V = 0.7;
const PERSON_ORDER = [0, 2, 4, 1, 3, 5];
/* The six product beam colors (rentdirect, rentID, rentshield, rentai, rentsuccess,
   rentacademy). Each outbound journey borrows one so the full palette flows OUT of the hub. */
const OUTBOUND_COLORS = ["#14E0A5", "#29B6E8", "#F5455F", "#4597F8", "#6A4BFF", "#FF9F0A"];
const JOURNEYS = PERSON_ORDER.map((p, pos) => {
    const dy = DEVICE_Y[RENTERS[p].device];
    const py = RENTERS[p].y;
    const vy = VEHICLES[p].y;
    const base = RELAY_BASE + pos * JOURNEY_STRIDE;
    /* Absolute launch of each segment: previous segment's travel + a pause held at the node. */
    const dS1 = base;
    const dS2 = dS1 + DUR_HD + PAUSE;
    const dS3 = dS2 + DUR_DP + PAUSE;
    const dS4 = dS3 + DUR_PV + PAUSE_V;
    const dS5 = dS4 + DUR_PV + PAUSE;
    const dS6 = dS5 + DUR_DP + PAUSE;
    return {
        person: p,
        color: OUTBOUND_COLORS[pos % OUTBOUND_COLORS.length],
        segHubDevice: `M ${HUB.x} ${HUB.y} ${sCurveTo(HUB.x, HUB.y, DEVICE_X, dy)}`,
        segDevicePerson: `M ${DEVICE_X} ${dy} ${sCurveTo(DEVICE_X, dy, RENTER_X, py)}`,
        segPersonVehicle: `M ${RENTER_X} ${py} L ${VEHICLE_X} ${vy}`,
        segVehiclePerson: `M ${VEHICLE_X} ${vy} L ${RENTER_X} ${py}`,
        segPersonDevice: `M ${RENTER_X} ${py} ${sCurveTo(RENTER_X, py, DEVICE_X, dy)}`,
        segDeviceHub: `M ${DEVICE_X} ${dy} ${sCurveTo(DEVICE_X, dy, HUB.x, HUB.y)}`,
        dS1,
        dS2,
        dS3,
        dS4,
        dS5,
        dS6,
    };
});
/* Opening inbound burst — request: during the initial sequence, every other person (rows 0, 2,
   4) sends a single beam back to the Rental Brand hub. Each is a two-leg chain, person -> device
   -> hub, in brand green (green is the "returning to the hub" color). It plays ONCE (see
   .seq-open-* in globals.css), staggered top-to-bottom, and each row's burst finishes on that
   row's person<->device rail BEFORE that row's outbound relay journey arrives there.

   The people/vehicle rails are now driven by the segmented relay ALONE (the old concurrent
   mid-loop that used to share these rails has been removed). Because a single relay journey's
   legs are sequential and consecutive journeys always occupy DIFFERENT rows (PERSON_ORDER cycles
   all six before repeating, 30s apart), at most one beam is ever on a given person or vehicle
   line at a time — so two beams can never share a line. */
const OPEN_ROWS = [0, 2, 4];
const OPEN_BASE = 2.6; // people have slid into place by ~2.6s (entrance HOLD + MOVE)
const OPEN_STRIDE = 0.75;
const OPENING = OPEN_ROWS.map((p, k) => {
    const dy = DEVICE_Y[RENTERS[p].device];
    const py = RENTERS[p].y;
    const start = OPEN_BASE + k * OPEN_STRIDE;
    return {
        person: p,
        segPersonDevice: `M ${RENTER_X} ${py} ${sCurveTo(RENTER_X, py, DEVICE_X, dy)}`,
        segDeviceHub: `M ${DEVICE_X} ${dy} ${sCurveTo(DEVICE_X, dy, HUB.x, HUB.y)}`,
        dPD: start,
        dDH: start + DUR_DP + PAUSE,
    };
});
function pct(value, total) {
    return `${(value / total) * 100}%`;
}
export function HomeVerifyNetwork() {
    const rootRef = useRef(null);
    const logoRefs = useRef([]);
    const [beamStarts, setBeamStarts] = useState(() => PRODUCTS.map((p) => p.beamStart));
    /* Entrance progress for the people, 0 = parked at the far-right (vehicle) column, 1 = settled
       at their connected column. Starts at 0 so the people open at the farthest right (matching
       SSR / no-JS); the timeline below holds them there through the opening, then glides them
       left to make room for the vehicles that fade in at the far right. */
    const [enter, setEnter] = useState(0);
    /* Which node's label is currently revealed by hovering its CONNECTING LINE (not the node
       itself — that stays handled by CSS :hover/:focus-within). `p{i}` = product i's beam,
       `d{i}` = the hub→device i rail. Null when no line is hovered. */
    const [activeLine, setActiveLine] = useState(null);
    /* Measure each logo's visible wordmark end (in px, relative to the card) and convert to
       viewBox-x units. The SVG uses preserveAspectRatio="none", so a fixed viewBox-x drifts
       from the HTML logos as the card resizes; measuring re-anchors the beams to the words
       at every width. Runs on mount, on each logo load, and on any resize. */
    const measure = useCallback(() => {
        const root = rootRef.current;
        if (!root)
            return;
        const rootRect = root.getBoundingClientRect();
        if (!rootRect.width)
            return;
        setBeamStarts((prev) => {
            let changed = false;
            const next = PRODUCTS.map((p, i) => {
                const img = logoRefs.current[i];
                if (!img || !img.width)
                    return prev[i];
                const r = img.getBoundingClientRect();
                const visibleRightPx = r.left - rootRect.left + p.contentFrac * r.width;
                const vb = (visibleRightPx / rootRect.width) * VB_W;
                if (Math.abs(vb - prev[i]) > 0.25)
                    changed = true;
                return vb;
            });
            return changed ? next : prev;
        });
    }, []);
    useEffect(() => {
        measure();
        const root = rootRef.current;
        if (!root)
            return;
        const ro = new ResizeObserver(() => measure());
        ro.observe(root);
        return () => ro.disconnect();
    }, [measure]);
    /* People entrance. Anchored to the SAME moment the build sequence arms (the `.seq-on` class
       appearing on an ancestor), so the leftward slide lines up with the vehicles fading in
       (CSS delays those from ~2.95s after seq-on). We hold the people at the far right through
       the opening, then ease them left over MOVE_MS. Because the device->person rail is
       recomputed from `enter` on every frame, the people stay connected to their node the whole
       way. Reduced-motion: snap straight to the settled state. Plays once. */
    useEffect(() => {
        const root = rootRef.current;
        if (!root)
            return;
        const reduce = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduce) {
            setEnter(1);
            return;
        }
        const HOLD_MS = 1750;
        const MOVE_MS = 850;
        const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
        let cancelled = false;
        let moveRaf = 0;
        let armRaf = 0;
        let start = 0;
        const step = (now) => {
            if (cancelled)
                return;
            if (!start)
                start = now;
            const elapsed = now - start;
            if (elapsed <= HOLD_MS) {
                moveRaf = requestAnimationFrame(step);
                return;
            }
            const p = Math.min(1, (elapsed - HOLD_MS) / MOVE_MS);
            setEnter(easeInOut(p));
            if (p < 1)
                moveRaf = requestAnimationFrame(step);
        };
        const waitForArm = () => {
            if (cancelled)
                return;
            if (root.closest(".seq-on")) {
                moveRaf = requestAnimationFrame(step);
            }
            else {
                armRaf = requestAnimationFrame(waitForArm);
            }
        };
        armRaf = requestAnimationFrame(waitForArm);
        return () => {
            cancelled = true;
            cancelAnimationFrame(moveRaf);
            cancelAnimationFrame(armRaf);
        };
    }, []);
    const paths = makePaths(beamStarts);
    /* People x (viewBox units) interpolated from the entrance progress, and the device->person
       rails recomputed to terminate on it — so the rails follow the people as they slide in. */
    const personX = VEHICLE_X + (RENTER_X - VEHICLE_X) * enter;
    const deviceRenterRails = makeDeviceRenter(personX);
    return (
    /* On phones only the product → Rental Brand half is shown: the root is widened so the
       left ~330 viewBox units fill the card, and everything right of the hub is hidden. */
    <div ref={rootRef} className="absolute inset-y-0 left-0 w-[188%] sm:inset-0 sm:w-auto">
      {/* Beam layer. */}
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} fill="none" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" aria-hidden="true">
        <defs>
          <filter id="verifyBeamGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2.2" result="b"/>
            <feMerge>
              <feMergeNode in="b"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          {/* Product-beam gradient (green → cyan → Turo purple) for the renter→vehicle
            beams and the glowing vehicle borders. */}
          <linearGradient id="verifyVehicleGrad" x1="0" y1="0" x2={VB_W} y2="0" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#14E0A5"/>
            <stop offset="0.5" stopColor="#29B6E8"/>
            <stop offset="1" stopColor="#593CFB"/>
          </linearGradient>
        </defs>

        {/* Faint static rails so the structure reads even between beam pulses. The product
            beams, hub->device rails AND the device->person rails are ALL always on, so every
            person stays visibly connected to their Online / In person node at all times. */}
        {paths.map((d, i) => (<path key={`base-${i}`} d={d} stroke="#FFFFFF" strokeOpacity={0.12} strokeWidth={1.25}/>))}
        <g className="max-sm:hidden">
        {[...HUB_DEVICE, ...deviceRenterRails].map((d, i) => (<path key={`base-right-${i}`} d={d} stroke="#FFFFFF" strokeOpacity={0.12} strokeWidth={1.25}/>))}
        {/* Renter -> vehicle rails: revealed only once the green relay reaches the renters. */}
        {RENTER_VEHICLE.map((d, i) => (<path key={`base-veh-${i}`} d={d} stroke="#FFFFFF" strokeOpacity={0.12} strokeWidth={1.25} className="seq-vehicle" style={{ ["--v-i"]: i }}/>))}
        </g>

        <g filter="url(#verifyBeamGlow)">
          {/* Product beams flowing into the hub (phase 3). */}
          {paths.map((d, i) => (<path key={`beam-${i}`} d={d} pathLength={1} stroke={PRODUCTS[i].color} strokeWidth={2.5} strokeLinecap="round" strokeDasharray="0.18 0.82" className="seq-beam seq-beam-product"/>))}

          {/* Segmented relay. Per person, six short beams hop node-to-node with a pause at
            each stop: OUT in the product color (hub -> device -> person -> vehicle), then
            PURPLE back (vehicle -> person -> device), then GREEN back (device -> hub, the only
            beam that re-enters the hub). Every leg travels at the same calm product-beam
            speed; the gaps between segment delays are the pauses held at the device, person
            and vehicle. New journeys launch every 3s so ~3-4 are in flight at once. */}
          {JOURNEYS.map((j) => (<g key={`journey-${j.person}`} className="max-sm:hidden">
              <path d={j.segHubDevice} pathLength={1} stroke={j.color} strokeWidth={2.5} strokeLinecap="round" strokeDasharray="0.16 2" className="seq-beam seq-seg-long" style={{ ["--d"]: `${j.dS1}s` }}/>
              <path d={j.segDevicePerson} pathLength={1} stroke={j.color} strokeWidth={2.5} strokeLinecap="round" strokeDasharray="0.16 2" className="seq-beam seq-seg-med" style={{ ["--d"]: `${j.dS2}s` }}/>
              <path d={j.segPersonVehicle} pathLength={1} stroke={j.color} strokeWidth={2.5} strokeLinecap="round" strokeDasharray="0.16 2" className="seq-beam seq-seg-veh" style={{ ["--d"]: `${j.dS3}s` }}/>
              <path d={j.segVehiclePerson} pathLength={1} stroke={TURO_PURPLE} strokeWidth={2.5} strokeLinecap="round" strokeDasharray="0.16 2" className="seq-beam seq-seg-veh" style={{ ["--d"]: `${j.dS4}s` }}/>
              <path d={j.segPersonDevice} pathLength={1} stroke={TURO_PURPLE} strokeWidth={2.5} strokeLinecap="round" strokeDasharray="0.16 2" className="seq-beam seq-seg-med" style={{ ["--d"]: `${j.dS5}s` }}/>
              <path d={j.segDeviceHub} pathLength={1} stroke={GREEN} strokeWidth={2.5} strokeLinecap="round" strokeDasharray="0.16 2" className="seq-beam seq-seg-long" style={{ ["--d"]: `${j.dS6}s` }}/>
            </g>))}

          {/* Opening inbound burst: during the intro, every other person (rows 0, 2, 4) sends a
            single green beam back to the Rental Brand hub (person -> device -> hub). Plays ONCE,
            and each row's burst clears that row's person<->device rail BEFORE the row's outbound
            relay journey arrives — so no two beams ever share a person/vehicle line. */}
          {OPENING.map((o) => (<g key={`open-${o.person}`} className="max-sm:hidden">
              <path d={o.segPersonDevice} pathLength={1} stroke={GREEN} strokeWidth={2.5} strokeLinecap="round" strokeDasharray="0.16 2" className="seq-beam seq-open-dp" style={{ ["--d"]: `${o.dPD}s` }}/>
              <path d={o.segDeviceHub} pathLength={1} stroke={GREEN} strokeWidth={2.5} strokeLinecap="round" strokeDasharray="0.16 2" className="seq-beam seq-open-long" style={{ ["--d"]: `${o.dDH}s` }}/>
            </g>))}
        </g>

        {/* Invisible wide hit-targets laid over the product beams and the hub→device rails.
            Hovering the LINE that leads to a node sets `data-active` on that node, revealing its
            label just like hovering the node itself. `pointerEvents: stroke` means only the (fat,
            transparent) line area is interactive, and the HTML nodes sit above these so node
            hovering is unaffected. */}
        <g fill="none" stroke="transparent" strokeWidth={20} strokeLinecap="round" style={{ pointerEvents: "stroke" }}>
          {paths.map((d, i) => (<path key={`hit-product-${i}`} d={d} onPointerEnter={() => setActiveLine(`p${i}`)} onPointerLeave={() => setActiveLine((v) => (v === `p${i}` ? null : v))}/>))}
          {HUB_DEVICE.map((d, i) => (<path key={`hit-device-${i}`} d={d} onPointerEnter={() => setActiveLine(`d${i}`)} onPointerLeave={() => setActiveLine((v) => (v === `d${i}` ? null : v))}/>))}
        </g>
      </svg>

      {/* Soft brand-tinted glow behind the hub — a gentle cyan→green halo that lifts the
            disc off the background. Kept subtle (low alpha, wide blur). */}
      <span aria-hidden="true" className="absolute h-44 w-44 -translate-x-1/2 -translate-y-1/2 rounded-full blur-2xl lg:h-56 lg:w-56" style={{
            left: pct(HUB.x, VB_W),
            top: pct(HUB.y, VB_H),
            background: "radial-gradient(circle, rgba(51,247,190,0.22) 0%, rgba(41,182,232,0.14) 42%, rgba(41,182,232,0) 72%)",
        }}/>

      {/* Central "Rental Brand" hub — a faded-black disc with stacked white text, and a
            hover label describing the platform. */}
      <div className="node-hit absolute z-20 -translate-x-1/2 -translate-y-1/2" style={{ left: pct(HUB.x, VB_W), top: pct(HUB.y, VB_H) }} tabIndex={0} role="img" aria-label="Rental Brand ���� powered with everything needed to operate, manage and scale">
        <span className="flex aspect-square h-[72px] flex-col items-center justify-center rounded-full bg-[#0B0E13] px-2 text-center font-sans text-[11px] font-semibold uppercase leading-tight tracking-wide text-white ring-1 ring-white/15 shadow-[0_10px_30px_-6px_rgba(0,0,0,0.6)] lg:h-[92px] lg:text-xs">
          <span>Rental</span>
          <span>Brand</span>
        </span>
        <span className="node-tip absolute bottom-full left-1/2 mb-3 w-52 -translate-x-1/2 rounded-lg bg-black/90 px-3 py-2 text-center font-sans text-[11px] font-medium leading-snug text-gray-300 ring-1 ring-white/15" style={{ ["--tip-i"]: 0 }}>
          Powered with everything needed to operate, manage and scale.
        </span>
      </div>

      {/* Branded device row: small white circles holding the exact device mockups; labels
            stack on hover. */}
      {DEVICES.map((d, i) => (<div key={d.key} className="node-hit absolute z-20 -translate-x-1/2 -translate-y-1/2 max-sm:hidden" data-active={activeLine === `d${i}`} style={{ left: pct(DEVICE_X, VB_W), top: pct(DEVICE_Y[i], VB_H) }} tabIndex={0} role="img" aria-label={d.label}>
          <span className="relative block aspect-square h-[46px] overflow-hidden rounded-full bg-[#20283A] shadow-[0_6px_18px_-6px_rgba(0,0,0,0.6)] ring-2 ring-white/15 lg:h-[54px]">
            <Image src={d.src || "/placeholder.svg"} alt="" width={60} height={60} className="h-full w-full object-cover object-center"/>
          </span>
          <span className="node-tip absolute bottom-full left-1/2 mb-2 w-max -translate-x-1/2 whitespace-nowrap rounded-lg bg-black/90 px-3 py-1.5 text-center font-sans text-[11px] font-medium leading-snug text-gray-300 ring-1 ring-white/15" style={{ ["--tip-i"]: i + 1 }}>
            {d.label}
          </span>
        </div>))}

      {/* Renter cluster: real faces dressed for the renter type. They open at the far-right
            (vehicle) column and slide left into their connected column (`personX`, driven by the
            entrance timeline). Their device->person rail is recomputed to their live x every
            frame, so a person is ALWAYS joined to its Online / In person node, never detached.
            Hover reveals the type. Framed head-and-chest so the brand logo stays visible. */}
      {RENTERS.map((r, i) => (<div key={r.label} className="node-hit absolute z-20 -translate-x-1/2 -translate-y-1/2 max-sm:hidden" style={{ left: pct(personX, VB_W), top: pct(r.y, VB_H), ["--v-i"]: i }} tabIndex={0} role="img" aria-label={r.label}>
          <span className="relative block aspect-square h-[46px] overflow-hidden rounded-full bg-[#20283A] shadow-[0_6px_18px_-6px_rgba(0,0,0,0.6)] ring-2 ring-white/15 lg:h-[54px]">
            <Image src={r.src || "/placeholder.svg"} alt="" width={60} height={60} className="h-full w-full object-cover object-center"/>
          </span>
        </div>))}

      {/* Vehicle column: each renter shown with the vehicle they rent — circular, same size
            as the renter avatars, image filling the circle. Hidden until the green relay has
            reached the renters, then revealed (see `.seq-vehicle`). */}
      {VEHICLES.map((v, i) => (<div key={v.label} className="node-hit seq-vehicle absolute z-20 -translate-x-1/2 -translate-y-1/2 max-sm:hidden" style={{ left: pct(VEHICLE_X, VB_W), top: pct(v.y, VB_H), ["--v-i"]: i }} tabIndex={0} role="img" aria-label={v.label}>
          <span className="relative block aspect-square h-[46px] overflow-hidden rounded-full bg-[#20283A] shadow-[0_6px_18px_-6px_rgba(0,0,0,0.6)] ring-2 ring-white/15 lg:h-[54px]">
            <Image src={v.src || "/placeholder.svg"} alt="" width={60} height={60} className="h-full w-full object-cover object-center"/>
          </span>
        </div>))}

      {/* Gap labels: one badge per row, centered in the gap between each person and their
            vehicle (at the midpoint MID_X). Always visible once its row's vehicle has appeared
            — the parent `.seq-vehicle` fades it in at phase 5 and keeps it. Shows only the
            renter title. */}
      {RENTERS.map((r, i) => (<div key={`gap-${r.label}`} className="seq-vehicle pointer-events-none absolute z-30 -translate-x-1/2 -translate-y-1/2 max-sm:hidden" style={{ left: pct(MID_X, VB_W), top: pct(r.y, VB_H), ["--v-i"]: i }} aria-hidden="true">
          <span className="block whitespace-nowrap rounded-md bg-black/90 px-2 py-1 text-center font-sans text-[10px] font-medium leading-none text-gray-300 ring-1 ring-white/15">
            {r.short}
          </span>
        </div>))}

      {/* LEFT column: the full horizontal product logos, left-aligned at their natural
            aspect ratio. They light up once in phase 2 of the build sequence (seq-logo) and
            stay at full brightness. --i drives the per-logo stagger. */}
      {PRODUCTS.map((p, i) => (<span key={p.name} className="node-hit seq-logo absolute z-30 flex -translate-y-1/2 items-center justify-start" data-active={activeLine === `p${i}`} style={{
                left: pct(LOGO_X, VB_W),
                top: pct(ROW_Y[i], VB_H),
                height: `${(10 * p.scale).toFixed(2)}%`,
                minHeight: `${Math.round(34 * p.scale)}px`,
                ["--i"]: i,
            }} tabIndex={0} role="img" aria-label={`${p.name} — ${p.tip}`}>
          <img ref={(el) => {
                logoRefs.current[i] = el;
            }} onLoad={measure} src={p.src || "/placeholder.svg"} alt="" className="h-full w-auto max-w-none object-contain"/>
          <span className="node-tip absolute left-full top-1/2 ml-3 w-max -translate-y-1/2 whitespace-nowrap rounded-lg bg-black/90 px-3 py-1.5 text-left font-sans text-[11px] font-medium leading-snug text-gray-300 ring-1 ring-white/15">
            {p.tip}
          </span>
        </span>))}
    </div>);
}
